import type { TCompanyResponse } from "@/api/companies";

import type { TGuidancePersistedState, TGuidanceStage, TTourId } from "./types";

const POST_ONBOARDING_TOUR_IDS: TTourId[] = ["sidebar-navigation-v1"];

export type TGuidanceStageInput = {
  companies: TCompanyResponse[] | null | undefined;
  guidanceState: TGuidancePersistedState;
};

export function getGuidanceStage({ companies, guidanceState }: TGuidanceStageInput): TGuidanceStage {
  const list = companies ?? [];

  if (list.length === 0) {
    return "no_company";
  }

  const hasCompletedOnboarding = list.some((company) => company.onboarding_completed);
  if (!hasCompletedOnboarding) {
    return "onboarding_in_progress";
  }

  const keyToursDone = POST_ONBOARDING_TOUR_IDS.every((tourId) => {
    const status = guidanceState.tours[tourId];
    return status === "completed" || status === "dismissed";
  });

  if (!keyToursDone) {
    return "onboarding_complete";
  }

  return "activated";
}

export function isStageEligible(stage: TGuidanceStage, requiredStages: TGuidanceStage[]): boolean {
  return requiredStages.includes(stage);
}

export function matchesRoute(pathname: string, routes: string[]): boolean {
  const normalizedPath = pathname.replace(/\/$/, "") || "/";

  return routes.some((route) => {
    if (route.includes("*")) {
      const pattern = route
        .split("*")
        .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("[^/]+");
      return new RegExp(`^${pattern}$`).test(normalizedPath);
    }

    const normalizedRoute = route.replace(/\/$/, "") || "/";
    if (normalizedRoute === "/app") {
      return normalizedPath === "/app";
    }

    return normalizedPath === normalizedRoute || normalizedPath.startsWith(`${normalizedRoute}/`);
  });
}

export function hasAnyChatThreads(companies: TCompanyResponse[] | null | undefined): boolean {
  if (!companies) {
    return false;
  }

  return companies.some((company) => {
    const sections = company.sections ?? [];
    return sections.some((section) => {
      const threads = section.threads ?? [];
      return threads.length > 0;
    });
  });
}
